import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Search, AlertTriangle, Layers, PackagePlus, Pencil } from "lucide-react";
import { brl, num, numStr, parseNum } from "@/lib/format";
import { ingredientCalc, defaultRecipeUnit, sourceKey, sourceUnits, unitLabel, PORTION } from "@/lib/recipeCalc";
import { Button, Input } from "@/components/kit";
import { QuickProductDialog } from "@/components/recipes/QuickProductDialog";
import { ProductQuickEdit } from "@/components/recipes/ProductQuickEdit";

import { norm } from "@/lib/search";
const priceLabel = (p) => (p.kind === "recipe" && !p.yield_qty ? `${brl(p.portion_cost)}/porção` : `${brl(p.unit_cost)}/${unitLabel(p.unit)}`);

function ProductSearch({ sources, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const all = useMemo(() => Object.values(sources), [sources]);
  const found = useMemo(() => (q.trim() ? all.filter((p) => norm(p.name).includes(norm(q.trim()))).slice(0, 10) : []), [all, q]);
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 muted" />
      <Input className="pl-10" placeholder="Buscar insumo ou preparo base para adicionar" value={q} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} data-testid="ingredient-search-input" />
      {open && found.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 card !p-1 shadow-xl max-h-72 overflow-y-auto" data-testid="ingredient-search-results">
          {found.map((p) => (
            <button key={`${p.kind}-${p.id}`} type="button" className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--brand-tint)]" onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(p); setQ(""); setOpen(false); }} data-testid={`ingredient-option-${p.id}`}>
              <div className="label flex items-center gap-1.5">{p.kind === "recipe" && <Layers size={13} style={{ color: "var(--brand)" }} />}{p.name}</div>
              <div className="caption">{p.category_name || "Sem categoria"} · {priceLabel(p)}</div>
            </button>
          ))}
        </div>
      )}
      {open && q.trim() && !found.length && <div className="caption mt-1">Nenhum insumo encontrado.</div>}
    </div>
  );
}

function NumCell({ label, value, onChange, testid, suffix }) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="caption">{label}</span>
      <div className="relative">
        <input className="input input-sm tnum text-right w-full" inputMode="decimal" value={value} onFocus={(e) => e.target.select()} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={suffix ? { paddingRight: 26 } : undefined} />
        {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 caption">{suffix}</span>}
      </div>
    </label>
  );
}

function SourceInfo({ src, ing }) {
  if (!src) return <div className="caption">{ing.kind === "recipe" ? "Preparo base não encontrado" : "Produto não encontrado no cadastro"}</div>;
  const base = src.kind === "recipe";
  return (
    <div className="caption flex flex-wrap items-center gap-1.5">
      {base && <span className="badge badge-brand !py-0" data-testid={`ingredient-base-badge-${ing.key}`}><Layers size={11} /> Preparo base</span>}
      {priceLabel(src)}{base && src.yield_qty ? ` · ${brl(src.portion_cost)}/porção` : ""}
    </div>
  );
}

function incompatibleText(src, ing) {
  if (src.kind === "recipe" && !src.yield_qty && ing.unit !== PORTION) return "Este preparo não tem rendimento em peso/volume · use porção";
  return `Unidade ${unitLabel(ing.unit)} incompatível com ${unitLabel(src.unit)} · custo não calculado`;
}

function IngredientRow({ ing, src, onChange, onRemove }) {
  const [editing, setEditing] = useState(false);
  const c = ingredientCalc({ ...ing, qty_gross: parseNum(ing.qty_gross), loss_pct: parseNum(ing.loss_pct) }, src);
  const units = [...new Set([...sourceUnits(src), ing.unit])];
  const onSaved = (p) => {
    if (!ingredientCalc({ ...ing, qty_gross: 1, loss_pct: 0 }, { ...src, unit: p.unit, unit_cost: p.unit_cost }).incompatible) return;
    onChange({ unit: defaultRecipeUnit(p.unit), qty_gross: "" });
    toast.warning("A unidade mudou · informe de novo a quantidade deste insumo.");
  };
  return (
    <div className="py-3 flex flex-col gap-2" data-testid={`ingredient-row-${ing.key}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0"><div className="label truncate">{src?.name || ing.product_name || "Insumo removido"}</div><SourceInfo src={src} ing={ing} /></div>
        {src?.kind === "product" && <button type="button" className="icon-btn" title="Corrigir custo ou unidade" onClick={() => setEditing((v) => !v)} data-testid={`ingredient-edit-${ing.key}`}><Pencil size={15} /></button>}
        <button type="button" className="icon-btn" onClick={onRemove} data-testid={`ingredient-remove-${ing.key}`}><Trash2 size={15} /></button>
      </div>
      {editing && <ProductQuickEdit src={src} ingKey={ing.key} onSaved={onSaved} onClose={() => setEditing(false)} />}
      <div className="grid grid-cols-2 sm:grid-cols-[1fr_100px_1fr_1fr_1fr] gap-2 items-end">
        <NumCell label="Qtd. bruta" value={ing.qty_gross} onChange={(v) => onChange({ qty_gross: v })} testid={`ingredient-gross-${ing.key}`} />
        <label className="flex flex-col gap-1"><span className="caption">Unidade</span>
          <select className="input input-sm" value={ing.unit} onChange={(e) => onChange({ unit: e.target.value })} data-testid={`ingredient-unit-${ing.key}`}>{units.map((u) => <option key={u} value={u}>{unitLabel(u)}</option>)}</select>
        </label>
        <NumCell label="Perda / FC" value={ing.loss_pct} onChange={(v) => onChange({ loss_pct: v })} testid={`ingredient-loss-${ing.key}`} suffix="%" />
        <div className="flex flex-col gap-1"><span className="caption">Qtd. líquida</span><div className="input input-sm flex items-center justify-end tnum" style={{ background: "var(--surface-alt)" }} data-testid={`ingredient-net-${ing.key}`}>{num(c.qtyNet)} {unitLabel(ing.unit)}</div></div>
        <div className="flex flex-col gap-1 col-span-2 sm:col-span-1"><span className="caption">Custo na receita</span><div className="input input-sm flex items-center justify-end tnum font-bold" style={{ background: "var(--brand-tint)" }} data-testid={`ingredient-cost-${ing.key}`}>{brl(c.cost)}</div></div>
      </div>
      <div className="caption">Fator de correção {num(c.factor, 2)}</div>
      {c.incompatible && <div className="caption flex items-center gap-1" style={{ color: "var(--danger)" }} data-testid={`ingredient-incompatible-${ing.key}`}><AlertTriangle size={12} /> {incompatibleText(src, ing)}</div>}
    </div>
  );
}

const newIngredient = (p) => ({
  key: crypto.randomUUID(), kind: p.kind, product_id: p.kind === "product" ? p.id : null, recipe_id: p.kind === "recipe" ? p.id : null,
  product_name: p.name, unit: p.kind === "recipe" && !p.yield_qty ? PORTION : defaultRecipeUnit(p.unit), qty_gross: "", loss_pct: "",
});

export default function IngredientsEditor({ ingredients, setIngredients, sources }) {
  const [creating, setCreating] = useState(false);
  const add = (p) => setIngredients((xs) => [...xs, newIngredient(p)]);
  const update = (key, patch) => setIngredients((xs) => xs.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  return (
    <div className="flex flex-col gap-3" data-testid="ingredients-editor">
      <ProductSearch sources={sources} onPick={add} />
      <button type="button" className="self-start caption font-semibold underline flex items-center gap-1" style={{ color: "var(--brand)" }} onClick={() => setCreating(true)} data-testid="open-quick-product-button">
        <PackagePlus size={14} /> Não encontrou? Cadastrar novo produto ou insumo
      </button>
      <QuickProductDialog open={creating} onOpenChange={setCreating} onCreated={(p) => add({ ...p, kind: "product" })} />
      {!ingredients.length && <p className="caption py-4">Nenhum insumo. Busque acima e adicione os ingredientes ou preparos base da receita.</p>}
      <div className="divide">
        {ingredients.map((i) => <IngredientRow key={i.key} ing={i} src={sources[sourceKey(i)]} onChange={(p) => update(i.key, p)} onRemove={() => setIngredients((xs) => xs.filter((x) => x.key !== i.key))} />)}
      </div>
      {ingredients.length > 0 && <Button variant="ghost" size="sm" icon={Plus} className="self-start" onClick={() => document.querySelector('[data-testid="ingredient-search-input"]')?.focus()} data-testid="add-ingredient-button">Adicionar outro insumo</Button>}
    </div>
  );
}

export const toFormIngredient = (i) => ({
  key: i.id || crypto.randomUUID(), kind: i.kind || "product", product_id: i.product_id, recipe_id: i.recipe_id,
  product_name: i.product_name, unit: i.unit, qty_gross: numStr(i.qty_gross), loss_pct: numStr(i.loss_pct),
});
